import { CameraOutlined, CloseOutlined } from '@ant-design/icons'
import {
  DrawerProps as AntdDrawerProps,
  Avatar,
  Button,
  Col,
  Drawer,
  Form,
  Row,
  Upload,
} from 'antd'
import { ReactNode, useMemo, useState } from 'react'
import { createRoot } from 'react-dom/client'
import styled from 'styled-components'
import { theme } from '../../config/style'
import { uploadPhotoFile } from '../../services/upload/upload.service'
import { useArrayWatch } from '../../utils/useArrayWatch'
import { Heading2 } from '../StyledComponents/StyledComponents'
import { DrawerProps } from './CustomDrawer.interfaces'
import { CustomLoginInput, CustomMobilePrefixSelect } from './Elements'

const StyledForm = styled(Form)`
  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-form-item-control-input {
    border: none !important;
  }

  & .ant-form-item-control-input-content {
    border: none !important;
  }

  & .ant-input,
  & .ant-input-number,
  & .ant-select {
    border: none !important;
    transition: none !important;
  }

  & .ant-input input {
    transition: none !important;
  }

  & .ant-input:hover,
  & .ant-input:focus,
  & .ant-input:active,
  & .ant-input-number:hover,
  & .ant-input-number:focus,
  & .ant-input-number:active {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    transition: none !important;
  }

  /* Remove borders from all validation states */
  & .ant-form-item-has-error .ant-input,
  & .ant-form-item-has-warning .ant-input,
  & .ant-form-item-has-success .ant-input,
  & .ant-form-item-has-feedback .ant-input {
    border: none !important;
  }

  /* Hide validation feedback icons */
  & .ant-form-item-feedback-icon {
    display: none !important;
  }

  /* Style for error messages */
  & .ant-form-item-explain-error {
    color: #ff4d4f; /* Ant Design default error color */
    opacity: 0.6;
    margin-top: 4px; /* Add some spacing above the error message */
    font-size: 12px; /* Adjust font size if needed */
  }

  & .ant-form-item-has-error .ant-select .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
  }
`

export interface PersonalInfo {
  photo?: File | null
  photoUrl?: string
  firstName?: string
  lastName?: string
  email: string
  mobilePrefix: string
  mobile: string
}

export interface MobileDrawerProps extends DrawerProps {
  title?: ReactNode
  onFinish?: (info: PersonalInfo) => void
  getContainer?: AntdDrawerProps['getContainer']
  initialFirstName?: string
  initialLastName?: string
  initialEmail?: string
  initialMobilePrefix?: string
  initialMobile?: string
  initialPhotoUrl?: string
  loginToken?: string
}

export const MobileDrawer = ({ ...props }: MobileDrawerProps) => {
  const onClose = props.onClose || (() => null)
  const onFinish = props.onFinish || (() => null)
  const [form] = Form.useForm()
  const [tel, tel_prefix, email, firstName, lastName] = useArrayWatch<
    [string, string, string, string, string]
  >(['tel', 'tel_prefix', 'email', 'firstName', 'lastName'], form)
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string>(props.initialPhotoUrl || '')
  const [uploadedPhotoUrl, setUploadedPhotoUrl] = useState<string | undefined>(
    props.initialPhotoUrl,
  )
  const [hasUploadedNewImage, setHasUploadedNewImage] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState(false)
  const [emailTouched, setEmailTouched] = useState(false)
  const [phoneTouched, setPhoneTouched] = useState(false)
  const { isEmailInvalid, isPhoneInvalid, valid } = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const currentIsEmailInvalid = !email || !emailRegex.test(email)

    // Phone validation: allow both with and without leading 0, but must be 9-10 digits
    const phoneDigits = tel?.replace(/^0/, '') || '' // Remove leading 0 if present
    const currentIsPhoneInvalid = !tel || !tel_prefix || phoneDigits.length < 9

    const currentValid = !currentIsEmailInvalid && !currentIsPhoneInvalid

    return {
      isEmailInvalid: currentIsEmailInvalid,
      isPhoneInvalid: currentIsPhoneInvalid,
      valid: currentValid,
    }
  }, [tel, tel_prefix, email])
  const [open, setOpen] = useState<boolean>()

  function handleBeforeClose() {
    setOpen(false)
    setTimeout(() => {
      onClose()
    }, 200)
  }

  const handlePhotoChange = async (info: any) => {
    const file = info.file.originFileObj || info.file

    if (file) {
      // Validate file extension
      const fileName = file.name.toLowerCase()
      const allowedExtensions = ['.jpg', '.jpeg', '.png', '.heic', '.heif']
      const isValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext))

      if (!isValidExtension) {
        // Silently reject invalid file types
        return
      }

      // Show preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
      setPhotoFile(file)

      // Upload immediately when user selects a photo
      setIsUploading(true)
      try {
        const uploadedUrl = await uploadPhotoFile(file, props.loginToken)
        setUploadedPhotoUrl(uploadedUrl)
        setHasUploadedNewImage(true)
      } catch (error) {
        // Silent fail - continue without uploaded URL
        console.error('Photo upload failed:', error)
        setHasUploadedNewImage(true)
      } finally {
        setIsUploading(false)
      }
    }
  }

  return (
    <Drawer
      open={open ?? props.open}
      onClose={() => handleBeforeClose()}
      placement="bottom"
      getContainer={props.getContainer || false}
      height="80%"
      headerStyle={{
        borderBottom: 0,
        zIndex: 1 /* boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.25)' */,
      }}
      title={
        <Row justify="center" align="middle" style={{ marginTop: '1rem' }}>
          <Col span={2}></Col>
          <Col span={20} style={{ textAlign: 'center' }}>
            <Heading2 style={{ color: theme.haupBlue }}>
              {props.title || 'Personal information'}
            </Heading2>
          </Col>
          <Col span={2}>
            <CloseOutlined
              style={{ fontSize: '20px', color: '#00000050' }}
              onClick={() => handleBeforeClose()}
            />
          </Col>
        </Row>
      }
      contentWrapperStyle={{
        borderRadius: '20px 20px 0 0',
        overflow: 'hidden',
      }}
      drawerStyle={{ background: theme.haupBlueSecondary }}
      closeIcon={null}
      // maskStyle={{ background: 'transparent' }}
      bodyStyle={{ position: 'relative' }}
      style={{
        maxWidth: '500px',
        margin: '0 auto',
      }}
    >
      <StyledForm form={form}>
        {/* Photo Upload */}
        <Form.Item style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Upload
            accept=".jpg,.jpeg,.png,.heic,.heif"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handlePhotoChange}
            disabled={isUploading}
            customRequest={({ file, onSuccess }) => {
              setTimeout(() => {
                onSuccess?.('ok')
              }, 0)
            }}
          >
            <div
              style={{
                position: 'relative',
                display: 'inline-block',
                cursor: isUploading ? 'not-allowed' : 'pointer',
              }}
            >
              <Avatar
                size={100}
                src={photoPreview}
                style={{
                  backgroundColor: '#1E40AF',
                  fontSize: '40px',
                  color: '#fff',
                  opacity: isUploading ? 0.6 : 1,
                }}
              >
                {!photoPreview &&
                  (() => {
                    const firstInitial = firstName?.[0]?.toUpperCase() || ''
                    const lastInitial = lastName?.[0]?.toUpperCase() || ''
                    return firstInitial + lastInitial || 'AC'
                  })()}
              </Avatar>
              {isUploading && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                  }}
                >
                  Uploading...
                </div>
              )}
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  backgroundColor: '#1E40AF',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                }}
              >
                <CameraOutlined style={{ color: 'white', fontSize: '16px' }} />
              </div>
            </div>
          </Upload>
        </Form.Item>

        {/* First Name */}
        <Form.Item name="firstName" initialValue={props.initialFirstName || ''}>
          <CustomLoginInput
            placeholder="First name (optional)"
            style={{ width: '100%', height: 70 }}
            autoComplete="off"
          />
        </Form.Item>

        {/* Last Name */}
        <Form.Item name="lastName" initialValue={props.initialLastName || ''}>
          <CustomLoginInput
            placeholder="Last name (optional)"
            style={{ width: '100%', height: 70 }}
            autoComplete="off"
          />
        </Form.Item>

        {/* Email */}
        <Form.Item
          name="email"
          initialValue={props.initialEmail || ''}
          validateStatus={(emailTouched || email) && isEmailInvalid ? 'error' : ''}
          help={
            (emailTouched || email) && isEmailInvalid ? 'Please enter a valid email address.' : ''
          }
          className="email-form-item"
        >
          <CustomLoginInput
            placeholder="Email *"
            style={{
              width: '100%',
              height: 70,
              ...(props.initialEmail && { backgroundColor: '#f5f5f5', cursor: 'not-allowed' }),
            }}
            autoComplete="off"
            readOnly={!!props.initialEmail}
            onBlur={() => setEmailTouched(true)}
          />
        </Form.Item>

        {/* Phone Number */}
        <Form.Item className="phone-form-item">
          <Row gutter={8} wrap={false}>
            <Col>
              <Form.Item noStyle name="tel_prefix" initialValue={props.initialMobilePrefix || '66'}>
                <CustomMobilePrefixSelect
                  style={{ minWidth: 150, maxWidth: 200 }}
                  disabled={!!props.initialMobile}
                />
              </Form.Item>
            </Col>
            <Col flex="auto">
              <Form.Item
                noStyle
                name="tel"
                initialValue={props.initialMobile || ''}
                validateStatus={phoneTouched && isPhoneInvalid ? 'error' : ''}
              >
                <CustomLoginInput
                  style={{
                    width: '100%',
                    height: 70,
                    ...(props.initialMobile && {
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed',
                    }),
                  }}
                  placeholder="9xxxxxxxx *"
                  maxLength={10}
                  autoComplete="off"
                  readOnly={!!props.initialMobile}
                  onBlur={() => setPhoneTouched(true)}
                />
              </Form.Item>
            </Col>
          </Row>
          {phoneTouched && isPhoneInvalid && (
            <div style={{ color: '#ff4d4f', opacity: 0.6, marginTop: 4, fontSize: 12 }}>
              Please enter a valid phone number (9 digits minimum)
            </div>
          )}
        </Form.Item>

        {/* Continue Button */}
        <Form.Item style={{ marginTop: '2rem' }}>
          <Button
            block
            type="primary"
            shape="round"
            size="large"
            disabled={!valid}
            onClick={() => {
              if (!tel_prefix || !tel || !email) {
                return
              }

              // Ensure phone number starts with 0
              const normalizedTel = tel.startsWith('0') ? tel : `0${tel}`

              // Use uploadedPhotoUrl if new image was uploaded, otherwise use initialPhotoUrl for social login images
              const finalPhotoUrl = hasUploadedNewImage ? uploadedPhotoUrl : props.initialPhotoUrl

              const personalInfo: PersonalInfo = {
                photo: photoFile || undefined,
                photoUrl: finalPhotoUrl,
                firstName: firstName || undefined,
                lastName: lastName || undefined,
                email,
                mobilePrefix: tel_prefix,
                mobile: normalizedTel,
              }
              onFinish(personalInfo)
            }}
            style={{
              backgroundColor: valid ? '#0048B3' : '#D1D5DB',
              borderColor: valid ? '#0048B3' : '#D1D5DB',
              color: '#fff',
              height: '48px',
              cursor: valid ? 'pointer' : 'not-allowed',
              opacity: valid ? 1 : 0.6,
            }}
          >
            Continue
          </Button>
        </Form.Item>
      </StyledForm>
    </Drawer>
  )
}

export function popMobileDrawer(
  container?: HTMLDivElement | null,
  initialFirstName?: string,
  initialLastName?: string,
  initialEmail?: string,
  initialMobilePrefix?: string,
  initialMobile?: string,
  initialPhotoUrl?: string,
  loginToken?: string,
): Promise<PersonalInfo | null> {
  return new Promise((resolve, reject) => {
    if (!container) {
      return reject('Container Not Found.')
    }
    const root = createRoot(container)
    function detachResolve(info?: PersonalInfo) {
      root.unmount()
      return resolve(info || null)
    }
    root.render(
      <MobileDrawer
        open={true}
        onClose={() => detachResolve()}
        onFinish={(info) => detachResolve(info)}
        initialFirstName={initialFirstName}
        initialLastName={initialLastName}
        initialEmail={initialEmail}
        initialMobilePrefix={initialMobilePrefix}
        initialMobile={initialMobile}
        initialPhotoUrl={initialPhotoUrl}
        loginToken={loginToken}
      />,
    )
  })
}
